import React, { useState } from 'react';
import { getPhaseColor } from '../services/geminiService';

const phaseOrder = ['PLANNING', 'FOUNDATION', 'DEVELOPMENT', 'LAUNCH', 'GROWTH', 'SCALING'];

export const RoadmapPanel = ({ metadata = {}, nodes = [] }) => {
  const [expandedPhase, setExpandedPhase] = useState(null);

  if (!metadata || Object.keys(metadata).length === 0) {
    return (
      <div className="p-4 text-center text-dark-text-secondary">
        <p>No roadmap data available. Generate a workflow to see the roadmap.</p>
      </div>
    );
  }

  // Group nodes by phase
  const nodesByPhase = phaseOrder.reduce((acc, phase) => {
    acc[phase] = nodes.filter(n => n.phase === phase);
    return acc;
  }, {});

  // Filter phases that have nodes
  const activePhasesWithNodes = phaseOrder.filter(phase => nodesByPhase[phase].length > 0);

  return (
    <div className="p-4 space-y-4 max-h-96 overflow-y-auto">
      {/* Project Overview */}
      {metadata.totalEstimatedTime && (
        <div className="bg-dark-bg rounded-lg p-3 border border-dark-border">
          <h4 className="text-sm font-semibold text-dark-text-primary mb-2">Project Timeline</h4>
          <p className="text-xs text-dark-text-secondary">{metadata.totalEstimatedTime}</p>
        </div>
      )}

      {/* Critical Path */}
      {metadata.criticalPath && metadata.criticalPath.length > 0 && (
        <div className="bg-dark-bg rounded-lg p-3 border border-dark-border">
          <h4 className="text-sm font-semibold text-dark-text-primary mb-2">Critical Path</h4>
          <div className="space-y-1">
            {metadata.criticalPath.map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs text-dark-text-secondary">
                <span className="text-brand-primary">→</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phases Timeline */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-dark-text-primary">Phases</h4>
        <div className="space-y-1">
          {activePhasesWithNodes.map((phase) => (
            <div key={phase} className="space-y-1">
              <button
                onClick={() => setExpandedPhase(expandedPhase === phase ? null : phase)}
                className="w-full flex items-center justify-between p-2 rounded-lg bg-dark-bg border border-dark-border hover:border-brand-secondary/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: getPhaseColor(phase) }}
                  />
                  <span className="text-xs font-medium text-dark-text-primary">{phase}</span>
                  <span className="text-xs text-dark-text-secondary">
                    ({nodesByPhase[phase].length})
                  </span>
                </div>
                <span className="text-xs text-dark-text-secondary">
                  {expandedPhase === phase ? '▼' : '▶'}
                </span>
              </button>

              {expandedPhase === phase && (
                <div className="ml-4 space-y-1 bg-dark-bg/50 rounded-lg p-2 border border-dark-border/50">
                  {nodesByPhase[phase].map((node) => (
                    <div key={node.id || node.label} className="text-xs text-dark-text-secondary">
                      <div className="flex items-start gap-2">
                        <span className="text-brand-primary mt-0.5">•</span>
                        <div className="flex-1">
                          <p className="font-medium text-dark-text-primary">{node.label}</p>
                          <p className="text-xs text-dark-text-tertiary">
                            {node.assignedTo} • {node.priority}
                          </p>
                          {node.estimatedDuration && (
                            <p className="text-xs text-dark-text-tertiary">
                              ⏱ {node.estimatedDuration}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Key Milestones */}
      {metadata.keyMilestones && metadata.keyMilestones.length > 0 && (
        <div className="bg-dark-bg rounded-lg p-3 border border-dark-border">
          <h4 className="text-sm font-semibold text-dark-text-primary mb-2">Key Milestones</h4>
          <div className="space-y-2">
            {metadata.keyMilestones.map((milestone, idx) => (
              <div key={idx} className="text-xs">
                <p className="font-medium text-dark-text-primary">{milestone.name}</p>
                {milestone.targetDate && (
                  <p className="text-dark-text-secondary">📅 {milestone.targetDate}</p>
                )}
                {milestone.deliverables && milestone.deliverables.length > 0 && (
                  <ul className="text-dark-text-tertiary mt-1 ml-2 space-y-0.5">
                    {milestone.deliverables.map((d, i) => (
                      <li key={i}>✓ {d}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Team Roles */}
      {metadata.teamRoles && metadata.teamRoles.length > 0 && (
        <div className="bg-dark-bg rounded-lg p-3 border border-dark-border">
          <h4 className="text-sm font-semibold text-dark-text-primary mb-2">Team Roles</h4>
          <div className="space-y-2">
            {metadata.teamRoles.map((role, idx) => (
              <div key={idx} className="text-xs">
                <p className="font-medium text-dark-text-primary">{role.role}</p>
                {role.responsibilities && role.responsibilities.length > 0 && (
                  <ul className="text-dark-text-secondary mt-1 ml-2 space-y-0.5">
                    {role.responsibilities.slice(0, 2).map((r, i) => (
                      <li key={i}>• {r}</li>
                    ))}
                    {role.responsibilities.length > 2 && (
                      <li>• +{role.responsibilities.length - 2} more</li>
                    )}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Document Checklist */}
      {metadata.documentChecklist && metadata.documentChecklist.length > 0 && (
        <div className="bg-dark-bg rounded-lg p-3 border border-dark-border">
          <h4 className="text-sm font-semibold text-dark-text-primary mb-2">Documents Needed</h4>
          <div className="space-y-2">
            {metadata.documentChecklist.map((category, idx) => (
              <div key={idx} className="text-xs">
                <p className="font-medium text-dark-text-primary capitalize">{category.category}</p>
                {category.documents && category.documents.length > 0 && (
                  <ul className="text-dark-text-secondary mt-1 ml-2 space-y-0.5">
                    {category.documents.slice(0, 3).map((doc, i) => (
                      <li key={i}>☐ {doc}</li>
                    ))}
                    {category.documents.length > 3 && (
                      <li>☐ +{category.documents.length - 3} more</li>
                    )}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
